import { useMemo, useCallback, useRef } from 'react'
import { AgGridReact } from 'ag-grid-react'
import { ModuleRegistry, AllCommunityModule } from 'ag-grid-community'
import 'ag-grid-community/styles/ag-theme-alpine.css'
import './ClientRequestsTable.css'
import type {
  ColDef,
  GridReadyEvent,
  GridApi,
  ColumnResizedEvent,
  DragStoppedEvent,
  SortChangedEvent,
} from 'ag-grid-community'
import { Link, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { getClient } from '../../shared/api/clients.api'
import { useAuthStore } from '../auth/authStore'
import { AG_GRID_LOCALE_RU } from '../../shared/ag-grid/locale'
import { STATUS_LABELS } from './StatusEditor'
import { PageLoader } from '../../shared/ui/Spinner'
import { EmptyState } from '../../shared/ui/EmptyState'
import { ErrorMessage } from '../../shared/ui/ErrorMessage'
import type { Request, RequestStatus } from '../../shared/api/types'

ModuleRegistry.registerModules([AllCommunityModule])

// Экран «Проекты клиента» (Р12, решение заказчика 14.07): проекты убраны из
// карточки клиента в отдельный список, вход — KPI-карточка «Проекты» в
// показателях. Управление проектом (название/закупщик/итог) здесь НЕ
// дублируется — оно в шапке заявки.

const COL_STATE_KEY = (userId: string) => `crm_client_projects_cols_v1_${userId}`

interface RowData {
  id: string
  projectName: string
  requestNumber: string
  status: RequestStatus
  buyer: string
  realized: boolean | null
  itemsCount: number
  createdAt: string
}

export function ClientProjectsPage() {
  const { id } = useParams<{ id: string }>()
  const { user } = useAuthStore()

  const {
    data: client,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['clients', id],
    queryFn: () => getClient(id!),
    enabled: !!id,
  })

  const rowData = useMemo<RowData[]>(
    () =>
      (client?.requests ?? [])
        .filter((r: Request) => r.type === 'PROJECT')
        .map((r) => ({
          id: r.id,
          projectName: r.projectName || 'Проект без названия',
          requestNumber: r.requestNumber ?? '—',
          status: r.status,
          buyer: r.responsibleBuyer?.name || r.responsibleBuyer?.email || '—',
          realized: r.projectRealized ?? null,
          itemsCount: r._count?.items ?? 0,
          createdAt: r.createdAt,
        })),
    [client?.requests],
  )

  const colDefs = useMemo<ColDef<RowData>[]>(
    () => [
      {
        field: 'projectName',
        headerName: 'Проект',
        minWidth: 180,
        flex: 1,
        cellRenderer: ({ data }: { data: RowData }) => (
          <Link
            to={`/requests/${data.id}`}
            className="text-brand-600 hover:text-brand-700 font-medium text-sm"
          >
            {data.projectName}
          </Link>
        ),
      },
      {
        field: 'requestNumber',
        headerName: 'Заявка',
        minWidth: 110,
        cellRenderer: ({ data }: { data: RowData }) => (
          <Link
            to={`/requests/${data.id}`}
            className="text-brand-600 hover:text-brand-700 font-mono font-medium text-sm"
          >
            {data.requestNumber}
          </Link>
        ),
      },
      {
        field: 'status',
        headerName: 'Статус',
        minWidth: 150,
        valueFormatter: ({ value }: { value: RequestStatus }) => STATUS_LABELS[value] ?? value,
      },
      {
        field: 'buyer',
        headerName: 'Ответственный закупщик',
        minWidth: 180,
      },
      {
        field: 'realized',
        headerName: 'Итог',
        minWidth: 130,
        cellRenderer: ({ data }: { data: RowData }) =>
          data.realized == null ? (
            <span className="text-gray-400">—</span>
          ) : data.realized ? (
            <span className="rounded-lg bg-green-100 px-1.5 py-0.5 text-xs text-green-700">
              Реализован
            </span>
          ) : (
            <span className="rounded-lg bg-red-100 px-1.5 py-0.5 text-xs text-red-700">
              Не реализован
            </span>
          ),
      },
      {
        field: 'itemsCount',
        headerName: 'Позиций',
        minWidth: 90,
      },
      {
        field: 'createdAt',
        headerName: 'Создан',
        minWidth: 100,
        valueFormatter: ({ value }: { value: string }) =>
          new Date(value).toLocaleDateString('ru-RU'),
      },
    ],
    [],
  )

  const gridRef = useRef<AgGridReact>(null)

  const defaultColDef = useMemo<ColDef>(
    () => ({ sortable: true, resizable: true, suppressMovable: false }),
    [],
  )

  const saveState = useCallback(
    (api: GridApi) => {
      if (!user?.id) return
      try {
        localStorage.setItem(COL_STATE_KEY(user.id), JSON.stringify(api.getColumnState()))
      } catch {
        /* ignore */
      }
    },
    [user?.id],
  )

  const onGridReady = useCallback(
    (params: GridReadyEvent) => {
      if (!user?.id) return
      const saved = localStorage.getItem(COL_STATE_KEY(user.id))
      if (saved) {
        try {
          params.api.applyColumnState({ state: JSON.parse(saved), applyOrder: true })
        } catch {
          /* ignore */
        }
      }
    },
    [user?.id],
  )

  if (isLoading) return <PageLoader />
  if (error) return <ErrorMessage error={error} />
  if (!client) return <EmptyState title="Клиент не найден" />

  return (
    <div className="space-y-4">
      {/* Breadcrumb: Клиенты → клиент → Проекты */}
      <div className="text-sm text-gray-500 flex items-center gap-2">
        <Link to="/clients" className="hover:text-gray-900">
          Клиенты
        </Link>
        <span>›</span>
        <Link to={`/clients/${client.id}`} className="hover:text-gray-900">
          {client.name}
        </Link>
        <span>›</span>
        <span className="text-gray-900">Проекты</span>
      </div>

      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold text-gray-900">
          Проекты <span className="text-sm font-normal text-gray-400">({rowData.length})</span>
        </h1>
      </div>

      {rowData.length === 0 ? (
        <EmptyState title="Проектов пока нет" />
      ) : (
        <div className="crm-table ag-theme-alpine rounded-xl overflow-x-auto border border-gray-200 bg-white shadow-sm">
          <AgGridReact
            ref={gridRef}
            getRowId={({ data }) => data.id}
            rowData={rowData}
            columnDefs={colDefs}
            defaultColDef={defaultColDef}
            localeText={AG_GRID_LOCALE_RU}
            onGridReady={onGridReady}
            onDragStopped={(e: DragStoppedEvent) => saveState(e.api)}
            onColumnResized={(e: ColumnResizedEvent) => {
              if (e.finished) saveState(e.api)
            }}
            onSortChanged={(e: SortChangedEvent) => saveState(e.api)}
            domLayout="autoHeight"
            rowHeight={40}
            headerHeight={40}
            suppressDragLeaveHidesColumns
            suppressCellFocus
            suppressRowHoverHighlight={false}
          />
        </div>
      )}
    </div>
  )
}
